import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inExpo, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * El Gusano de Médula (models/marrow-worm.ts): it rears like a serpent and moves like one, its tail dragging.
 *
 * 'idle' (3.6 s) sways reared; 'walk' (2.0 s) slithers; 'attack' lunges and bites (0.55 s). The rule: 'dive'
 * (1.2 s) rears, then plunges head first into the floor, all of it under by 1.1 s (swap to `marrowWormBurrowed`);
 * 'surface' (1.3 s) bursts up out of the floor reared high, jaws wide (0.35 s), and crashes down onto the floor at
 * 0.8 s — the blow — ending in the dazed pose; 'stun' (and 'dazed', the same) is lying stunned, jaws slack.
 *
 * Conventions: the neck bones grow up and forward: +X bows them forward and down, −X rears them back; the tail
 * bones grow back along the floor: +X lifts them. Y turns swing the chain side to side. −X on jawA/jawB/jawC
 * opens each jaw.
 */

type B = 'root' | 'body' | 'tail1' | 'tail2' | 'tail3' | 'tail4' | 'neck1' | 'neck2' | 'head' | 'jawA' | 'jawB' | 'jawC';
type P = KeyPose<B>;

const jaws = (b: Bones<B>, o: number): void => {
  b.jawA.rot(-o);
  b.jawB.rot(-o);
  b.jawC.rot(-o);
};
const J = (o: number): P => ({ jawA: [-o, 0, 0], jawB: [-o, 0, 0], jawC: [-o, 0, 0] });
/** Lying stunned where it fell: the neck down along the floor, the head on its side. */
const DAZED: P = { root: [0, 0, 0, 0, -0.05, 0.2], body: [0.08, 0, 0.1], neck1: [0.95, 0.15, 0], neck2: [0.2, 0.1, 0], head: [-0.25, 0.2, 0.45], tail1: [0, -0.1, 0], tail2: [0, -0.15, 0], tail3: [0, 0.12, 0], tail4: [0, 0.2, 0], ...J(0.35) };
/** Under the floor (all of it below y = 0, the head down). */
const UNDER: P = { root: [0, 0, 0, 0, -2.9, 0.5], neck1: [0.2, 0, 0], neck2: [0.1, 0, 0], ...J(0) };

const tailWave = (b: Bones<B>, th: number, amt: number): void => {
  b.tail1.rot(0, 0.12 * amt * Math.sin(th - 1.0), 0);
  b.tail2.rot(0, 0.16 * amt * Math.sin(th - 1.8), 0);
  b.tail3.rot(0, 0.2 * amt * Math.sin(th - 2.6), 0);
  b.tail4.rot(0, 0.26 * amt * Math.sin(th - 3.4), 0);
};

const idle = (): Clip<B> =>
  clip<B>('idle', 3.6, true, (b, t) => {
    const p = phase(t, 3.6) * TAU;
    b.body.rot(0, 0.05 * Math.sin(p), 0);
    b.neck1.rot(0.04 * Math.sin(p * 2), 0.1 * Math.sin(p + 0.4), 0.05 * Math.sin(p));
    b.neck2.rot(0.05 * Math.sin(p * 2 + 0.5), 0.12 * Math.sin(p + 0.9), 0.04 * Math.sin(p + 0.6));
    b.head.rot(0.06 * Math.sin(p * 2 + 1), -0.15 * Math.sin(p + 1.4), 0);
    jaws(b, 0.08 + 0.1 * Math.max(0, Math.sin(p * 2 + 2)) ** 2);
    tailWave(b, p, 0.4);
  });

const walk = (dur = 2.0): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    b.root.pos(0.04 * Math.sin(th), 0, 0);
    b.body.rot(0, 0.12 * Math.sin(th), 0.04 * Math.sin(th));
    tailWave(b, th, 1.3);
    b.neck1.rot(0.15, -0.12 * Math.sin(th + 0.6), 0);
    b.neck2.rot(0.05, -0.1 * Math.sin(th + 1.2), 0);
    b.head.rot(-0.05, 0.1 * Math.sin(th + 1.8), 0);
    jaws(b, 0.06);
  });

const attack = (): Clip<B> =>
  clip<B>('attack', 1.3, false, (b, t) => {
    const rear: P = { body: [-0.05, 0, 0], neck1: [-0.3, 0, 0], neck2: [-0.25, 0, 0], head: [-0.2, 0, 0], ...J(0.55) };
    const bite: P = { root: [0, 0, 0, 0, 0, 0.3], body: [0.1, 0, 0], neck1: [0.55, 0, 0], neck2: [0.35, 0, 0], head: [0.15, 0, 0], ...J(-0.05) };
    timeline(b, t, [
      [0, {}],
      [0.4, rear, inOut],
      [0.55, bite, inExpo],
      [0.8, { ...bite, neck1: [0.5, 0, 0] }, outCubic],
      [1.3, {}, inOut],
    ], { head: 0.04 });
  }, (t) => {
    const q = Math.max(0, settle(t - 0.55, 3, 8)) * (t > 0.55 ? 1 : 0);
    return [1 + 0.03 * q, 1 - 0.04 * q, 1 + 0.03 * q];
  });

/** The rule, part one: it rears up, then plunges head first into the floor and is gone. */
const dive = (): Clip<B> =>
  clip<B>('dive', 1.2, false, (b, t) => {
    const rear: P = { root: [0, 0, 0, 0, 0.1, -0.1], neck1: [-0.35, 0, 0], neck2: [-0.3, 0, 0], head: [-0.25, 0, 0], tail2: [0.15, 0, 0], tail3: [0.2, 0, 0], ...J(0.4) };
    const plunge: P = { root: [0, 0, 0, 0, -0.9, 0.35], body: [0.35, 0, 0], neck1: [0.9, 0, 0], neck2: [0.5, 0, 0], head: [0.3, 0, 0], tail2: [0.25, 0, 0], tail3: [0.35, 0, 0], tail4: [0.4, 0, 0], ...J(0.1) };
    timeline(b, t, [
      [0, {}],
      [0.4, rear, inOut],
      [0.68, plunge, inExpo],
      [1.1, { ...UNDER, body: [0.4, 0, 0], tail3: [0.4, 0, 0], tail4: [0.5, 0, 0] }, inQuad],
      [1.2, { ...UNDER, body: [0.4, 0, 0], tail3: [0.4, 0, 0], tail4: [0.5, 0, 0] }],
    ], { head: 0.03, tail4: 0.06 });
  }, (t) => {
    const q = Math.max(0, settle(t - 0.68, 3, 8)) * (t > 0.68 ? 1 : 0);
    return [1 + 0.03 * q, 1 - 0.03 * q, 1 + 0.03 * q];
  }, 'Se hunde');

/** The rule, part two: it bursts up out of the floor reared high, jaws wide, and crashes down dazed. */
const surface = (): Clip<B> =>
  clip<B>('surface', 1.3, false, (b, t) => {
    const burst: P = { root: [0, 0, 0, 0, 0.25, -0.1], body: [-0.2, 0, 0], neck1: [-0.35, 0, 0], neck2: [-0.25, 0, 0], head: [-0.35, 0, 0], tail1: [0.25, 0, 0], tail2: [0.3, 0, 0], ...J(0.75) };
    timeline(b, t, [
      [0, { ...UNDER, neck1: [-0.3, 0, 0], neck2: [-0.2, 0, 0], head: [-0.3, 0, 0], ...J(0.4) }],
      [0.35, burst, outExpo],
      [0.6, { ...burst, root: [0, 0, 0, 0, 0.28, -0.12], neck1: [-0.4, 0, 0] }, outQuad],
      [0.8, { ...DAZED, root: [0, 0, 0, 0, -0.14, 0.25], ...J(0.6) }, inExpo],
      [1.0, { ...DAZED, ...J(0.45) }, outBack],
      [1.3, DAZED, inOut],
    ], { head: 0.03, tail3: 0.05, tail4: 0.08 });
  }, (t) => {
    const q = Math.max(0, settle(t - 0.8, 2.5, 7)) * (t > 0.8 ? 1 : 0);
    return [1 + 0.06 * q, 1 - 0.07 * q, 1 + 0.06 * q];
  }, 'Sale');

/** Lying dazed where it fell: twitching, jaws slack. */
const dazed = (name: string): Clip<B> =>
  clip<B>(name, 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    timeline(b, t, [[0, DAZED]]);
    b.head.rot(0.05 * Math.sin(p * 2), 0.08 * Math.sin(p), 0);
    b.neck2.rot(0.03 * Math.sin(p + 1), 0, 0);
    jaws(b, 0.08 * Math.sin(p * 3));
    b.tail4.rot(0.15 * Math.max(0, Math.sin(p * 2 + 1)) ** 3, 0, 0);
  }, undefined, 'Aturdido');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.5, 8) * span(t, 0, 0.04, outQuad);
    b.neck1.rot(-0.15 * q, 0.06 * q, 0);
    b.neck2.rot(-0.12 * q);
    b.head.rot(-0.2 * q, -0.1 * q, 0.08 * q);
    jaws(b, 0.35 * Math.max(0, q));
    b.tail3.rot(0.1 * q);
  });

/** Death (2.6 s): it rears and shrieks, then topples, the neck crashing down, and rolls onto its side, curling. */
const die = (): Clip<B> =>
  clip<B>('die', 2.6, false, (b, t) => {
    const shriek: P = { body: [-0.1, 0, 0], neck1: [-0.4, 0, 0.15], neck2: [-0.3, 0, 0], head: [-0.5, 0, 0], ...J(0.85) };
    const fallen: P = { root: [0, 0, 1.2, 0, 0.2, 0.2], body: [0.1, 0.2, 0], neck1: [1.2, 0.3, 0], neck2: [0.5, 0.2, 0], head: [0.2, 0.3, 0], tail1: [0, 0.3, 0], tail2: [0, 0.4, 0], tail3: [0, 0.5, 0], tail4: [0, 0.6, 0], ...J(0.5) };
    timeline(b, t, [
      [0, {}],
      [0.45, shriek, outExpo],
      [1.0, { ...shriek, neck1: [-0.3, 0.1, 0.2] }, inOut],
      [1.7, fallen, inExpo],
      [1.95, { ...fallen, root: [0, 0, 1.25, 0, 0.18, 0.2] }, outBack],
      [2.6, fallen, inOut],
    ], { head: 0.04, tail4: 0.08 });
    b.head.rot(0.04 * Math.sin(t * 50) * envelope(t, 0.4, 0.5, 0.9, 1.1), 0, 0);
  }, (t) => {
    const q = Math.max(0, settle(t - 1.7, 2.5, 7)) * (t > 1.7 ? 1 : 0);
    return [1 + 0.05 * q, 1 - 0.06 * q, 1 + 0.05 * q];
  });

/* ---------- under the floor: the mound ---------- */

type U = 'root' | 'ridge' | 'plates';
const burrowIdle = (): Clip<U> =>
  clip<U>('idle', 1.6, true, (b, t) => {
    const p = phase(t, 1.6) * TAU;
    b.ridge.pos(0, 0.02 * Math.sin(p), 0).scale(1 + 0.03 * Math.sin(p));
    b.plates.rot(0.06 * Math.sin(p + 1), 0, 0);
  });
const burrowWalk = (): Clip<U> =>
  clip<U>('walk', 0.8, true, (b, t) => {
    const p = phase(t, 0.8) * TAU;
    b.ridge.pos(0.02 * Math.sin(p), 0.035 * Math.abs(Math.sin(p)), 0).rot(0.03 * Math.sin(2 * p), 0.04 * Math.sin(p), 0.03 * Math.sin(p));
    b.plates.pos(0, 0.04 * Math.sin(2 * p), 0).rot(0.1 * Math.sin(2 * p + 1), 0, 0);
  });
const burrowDie = (): Clip<U> =>
  clip<U>('die', 1.2, false, (b, t) => {
    const k = span(t, 0, 1.2, inOut);
    b.ridge.pos(0, -0.4 * k, 0).scale(1 - 0.3 * k);
    b.plates.pos(0, -0.3 * k, 0);
  });

/** Clips by library kind (see ../rigs.ts). */
export const marrowWormClips = (): Clip[] => [idle(), walk(), attack(), hit(), dazed('stun'), die(), dive(), surface(), dazed('dazed')] as Clip[];
export const marrowWormBurrowedClips = (): Clip[] => [burrowIdle(), burrowWalk(), burrowDie()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { marrowWorm: marrowWormClips, marrowWormBurrowed: marrowWormBurrowedClips };
